import { useMemo, useState } from "react";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import PageHeader from "../../components/common/PageHeader";

const Faq = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const items = [
    {
      question: "What is the POS?",
      answer: [
        'POS stands for Point of Sale. It is the screen where your team creates orders, sends them to the kitchen, and takes payments.',
        'To open POS, click the top-right button labeled "POS Mode".',
      ],
    },
    {
      question: "How does the POS workflow operate?",
      answer: [
        "The POS flow starts by selecting a table or starting a takeaway order. You add items, confirm the order, and then send it to the kitchen. If payment is required first, you take payment before sending. If payment is allowed later, you can send first and collect payment at the end.",
      ],
    },
    {
      question: "What is the dine-in vs takeaway workflow?",
      answer: [
        "Dine-In: open a table, add items, and follow your POS payment mode.",
        "Pay First: take payment before sending the order to the kitchen.",
        "Pay Later: send the order to the kitchen first, then collect payment at the end.",
        "Takeaway: start a takeaway order, add items, and always pay first before the order is sent.",
        "In POS, you will see buttons like \"Dine-In\" and \"Takeaway\" when starting an order.",
      ],
    },
    {
      question: "What are floors, sections, and tables?",
      answer: [
        "Floors represent areas of your restaurant. Sections are smaller areas inside a floor. Tables are the actual seats your guests use. This layout helps your team find and manage orders quickly.",
        "Menu paths: Floorplan -> Floors, Floorplan -> Sections, Floorplan -> Tables.",
      ],
    },
    {
      question: "How do I create a floor?",
      answer: [
        "Go to Floorplan -> Floors and click \"Add Floor\".",
        "Fill in \"Floor Name\" and (optional) \"Sort Order\", keep \"Active\" on if needed, then click \"Add Floor\".",
      ],
    },
    {
      question: "How do I create a section?",
      answer: [
        "Go to Floorplan -> Sections and click \"Add Section\".",
        "Choose a \"Floor\", fill in \"Section Name\" and (optional) \"Sort Order\", keep \"Active\" on if needed, then click \"Add Section\".",
      ],
    },
    {
      question: "How do I create a table?",
      answer: [
        "Go to Floorplan -> Tables and click \"Add Table\".",
        "Select a \"Floor\" and \"Section\", then fill in \"Table Number\" and \"Capacity\", keep \"Active\" on if needed, and click \"Add Table\".",
      ],
    },
    {
      question: "How do I create categories?",
      answer: [
        "In this Edge app, categories are read-only.",
        "Create categories in Central Admin, then wait for Edge sync to pull them in.",
        "Menu path to view categories: Catalog -> Categories.",
      ],
    },
    {
      question: "How do I create items?",
      answer: [
        "In this Edge app, items are read-only.",
        "Create items in Central Admin, then wait for Edge sync to pull them in.",
        "Menu path to view items: Catalog -> Items.",
      ],
    },
    {
      question: "Why are newly added items from Central Admin not showing in POS?",
      answer: [
        "Items are synced from Central Admin to Edge on sync.",
        "Go to Settings -> Configure Store and click \"Sync Store\", then wait for sync to finish.",
        "If it still does not appear, refresh the page and confirm the item is active in Central Admin.",
        "Also check the item availability in Catalog -> Items. New items may be unavailable by default, and must be marked available in the items list before they show in POS.",
      ],
    },
    {
      question: "How do I create stations?",
      answer: [
        "Go to Catalog -> Stations and click \"Add Station\".",
        "Fill in \"Station Code\", \"Display Name\", \"Print Prefix\", and (optional) \"Sort\", keep \"Active\" on if needed, then click \"Create Station\".",
      ],
    },
    {
      question: "How do I disable Kitchen Display for a station?",
      answer: [
        "Go to Catalog -> Stations.",
        "Edit the station and uncheck \"Display Active\", then save.",
        "You can also toggle Display Active directly from the station list if the toggle is shown.",
        "To disable Kitchen Display for all stations, go to Settings -> General Settings and turn off \"Kitchen Display Use Stations\".",
      ],
    },
    {
      question: "How do I send notes to the kitchen?",
      answer: [
        "Open the order in POS and click the \"NOTES\" button.",
        "In the \"Order Notes\" dialog, enter your instructions and click \"Save\".",
        "Submit the order to the kitchen so the notes are included.",
      ],
    },
    {
      question: "How do printers work in Edge?",
      answer: [
        "Printers are registered in Settings -> Printers and mapped to stations.",
        "Kitchen tickets print based on station mapping, and receipt printing uses the Default Receipt Printer for the selected device.",
        "If a station has no printer set, kitchen tokens for that station can fail.",
      ],
    },
    {
      question: "How do I assign items to a station?",
      answer: [
        "Go to Catalog -> Station Assignment.",
        "Select a station under \"1. Active Stations\".",
        "Drag items from \"3. Item Pool\" into \"2. Assigned Items\" for that station.",
        "Use the \"Refresh\" button if you need to reload the lists.",
      ],
    },
    {
      question: "What is station assignment?",
      answer: [
        "Station Assignment is where you connect items to a specific station, like Kitchen or Bar.",
        "This helps items show on the correct kitchen printer or display.",
        "Menu path: Catalog -> Station Assignment.",
      ],
    },
    {
      question: "What is the catalog?",
      answer: [
        "The catalog is your menu. It contains categories and items. Your team uses the catalog to add the right items to each order.",
        "Menu paths: Catalog -> Categories, Catalog -> Items.",
      ],
    },
    {
      question: "What are stations?",
      answer: [
        "Stations group items by where they are prepared, like Kitchen, Bar, or Dessert. When stations are enabled, the system routes items to the right printer or display so each team sees what they need.",
        "Menu path: Catalog -> Stations.",
      ],
    },
    {
      question: "How do I place an order in POS?",
      answer: [
        "Select a table or start a takeaway order, then add items from the catalog. Review the order, apply any discounts if needed, and confirm it. The order is then sent to the kitchen and appears on the kitchen printer or display.",
        'In POS, you will see buttons like "Dine-In" and "Takeaway" when starting an order.',
      ],
    },
    {
      question: "What happens after an order is sent to the kitchen?",
      answer: [
        "The kitchen receives the order. The table status updates so the team can track progress. When items are ready, the order can be completed and payment can be taken if it was not already paid.",
      ],
    },
    {
      question: "What are kiosks and displays?",
      answer: [
        "Kiosks let customers place orders themselves. Kitchen and order displays show orders to staff without printing paper. These screens keep everyone updated in real time.",
        "Menu paths: Device Management -> Kiosks, Settings -> General Settings.",
        'In General Settings, you may see options like "Kitchen Display Use Stations", "Order Display Use Stations", and "Show Ready Orders Only".',
      ],
    },
    {
      question: "Where do I manage devices and terminals?",
      answer: [
        "Menu paths: Device Management -> Local Devices, Device Management -> Kiosks, Device Management -> Terminals.",
        'Common buttons include "Add Device", "Add Kiosk", "Add Terminal", and form actions like "Update Device", "Update Kiosk", or "Update Terminal".',
      ],
    },
    {
      question: "Where do I manage printers and receipt settings?",
      answer: [
        "Menu path: Settings -> Printers.",
        'You will see "Add Printer", and in the form the main action is "Add Printer" or "Update Printer". For receipts, use "Save Receipt Printer".',
      ],
    },
    {
      question: "Where do I configure the store and run sync?",
      answer: [
        "Menu path: Settings -> Configure Store.",
        'Buttons include "Configure Store", "Update Store", "Sync Store", and "Refresh Status".',
      ],
    },
    {
      question: "What should I do if something looks wrong or missing?",
      answer: [
        "First, refresh the page. If the issue continues, check that the device is paired and active. For printer or station issues, confirm the correct mappings in Settings.",
        'You may see status labels like "Active" and "Inactive". On kiosks you might also see "Locked".',
      ],
    },
    {
      question: "What does the \"Device Not Paired\" error mean in POS?",
      answer: [
        "This means the POS device is not paired yet.",
        "Go to Device Management -> Local Devices and make sure the device is created and active.",
        "Select the device from the top bar device dropdown (label: \"Device\") to pair it.",
        "Then try opening the table again.",
      ],
    },
    {
      question: "Why am I seeing an \"Order opened\" error?",
      answer: [
        "This usually means the table already has an open order.",
        "If another user already opened the table, this device cannot access it at the same time.",
        "The table can only be opened when no user currently has it open.",
        "Wait for the other user to close the table, then try again.",
        "If needed, refresh the page and try again.",
      ],
    },
  ];

  const handleToggle = (index) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!normalizedSearch) return items;
    return items.filter((item) => {
      if (item.question.toLowerCase().includes(normalizedSearch)) return true;
      return item.answer.some((line) =>
        line.toLowerCase().includes(normalizedSearch),
      );
    });
  }, [items, normalizedSearch]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="FAQ"
        subtitle="Simple answers to the most common questions."
      />

      <Card className="space-y-8">
        <div className="max-w-md">
          <Input
            label="Search"
            value={searchTerm}
            placeholder="Search the FAQ"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setOpenIndex(null);
            }}
          />
        </div>

        {filteredItems.length === 0 && (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No results found.
          </p>
        )}

        <div className="space-y-3">
          {filteredItems.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={item.question}
                className={[
                  "overflow-hidden rounded-2xl border transition-all",
                  isOpen
                    ? "border-indigo-200 bg-indigo-50/40 shadow-sm dark:border-indigo-900/60 dark:bg-indigo-950/20"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600 dark:hover:bg-slate-800/80",
                ].join(" ")}
              >
                <button
                  type="button"
                  className="flex w-full items-center gap-4 px-4 py-4 text-left sm:px-5"
                  onClick={() => handleToggle(index)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${index}`}
                >
                  <span
                    className={[
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors",
                      isOpen
                        ? "border-indigo-200 bg-indigo-100 text-indigo-700 dark:border-indigo-900/70 dark:bg-indigo-900/40 dark:text-indigo-200"
                        : "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300",
                    ].join(" ")}
                    aria-hidden="true"
                  >
                    <svg
                      className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-90" : "rotate-0"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </span>

                  <span className="flex-1 text-base font-semibold leading-6 text-slate-900 dark:text-slate-100">
                    {item.question}
                  </span>
                </button>

                {isOpen && (
                  <div
                    id={`faq-panel-${index}`}
                    role="region"
                    aria-label={item.question}
                    className="border-t border-slate-200 bg-white px-4 py-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300 sm:px-5"
                  >
                    <div className="space-y-3 pl-12">
                      {item.answer.map((line) => (
                        <p key={line} className="leading-6">
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default Faq;
